import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(
        "E-mail em falta",
        {
          description: "Por favor, insira o seu endereço de e-mail"
        }
      );
      return;
    }
    
    try {
      setLoading(true);
      await resetPassword(email);
      
      toast.success(
        "Redefinir e-mail enviado",
        {
          description: "Verifique o seu e-mail para obter instruções sobre como redefinir a sua palavra-passe."
        }
      );
      
      // Navigate back to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error(
        "Reset Failed",
        {
          description: error.message || "Falha ao enviar o e-mail de redefinição. Por favor, tente novamente."
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto shadow-lg">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4">
          <Shield className="h-12 w-12 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">Redefinir palavra-passe</CardTitle>
        <CardDescription className="text-center">
          Insira o seu endereço de e-mail e enviaremos instruções para redefinir a sua palavra-passe.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="nome@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <span className="animate-spin mr-2">
                  <Mail size={18} />
                </span>
                Enviando...
              </div>
            ) : (
              <div className="flex items-center">
                <Mail size={18} className="mr-2" />
                Enviar link de redefinição
              </div>
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col space-y-2">
        <div className="text-sm text-center text-muted-foreground">
          Lembra-se da sua palavra-passe?{" "}
          <Button 
            variant="link" 
            className="p-0 h-auto" 
            onClick={() => navigate('/login')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar ao login
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ForgotPasswordForm; 